import {
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Card,
  CardBody,
  Icon,
  Progress,
} from '@chakra-ui/react';
import { FiArrowLeft, FiLock } from 'react-icons/fi';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/client/components/shared/LoadingSpinner';
import { EmptyState } from '@/client/components/shared/EmptyState';
import { TaskCard } from '../components/TaskCard';
import { TaskDataEditModal } from '@/client/components/data/TaskDataEditModal';
import { formatters } from '@/client/utils/formatters';
import { hasMediaAttachments, getMediaCount } from '@/client/utils/taskDataAdapter';
import { useTaskGroupDataPage } from '../hooks';
import type { TaskData } from '@/lib/sdk/chatbot/types';
import type { TaskCardStatus } from '../components/TaskCard';

function getDisplayValue(task: TaskData): string {
  const parts: string[] = [];
  if (task.note) parts.push(task.note);
  if (hasMediaAttachments(task)) {
    const count = getMediaCount(task);
    parts.push(`📎 ${count} attachment${count !== 1 ? 's' : ''}`);
  }
  return parts.join(' • ') || '';
}

function getTaskStatus(task: TaskData): TaskCardStatus {
  if (!task.mutable) return 'blocked';
  if (task.rep_status == 1 || task.rep_status == -2) return 'completed';
  return 'pending';
}

export function TaskGroupDataPage() {
  const p = useTaskGroupDataPage();

  // Whether a task's value is created fresh or edited depends on whether it
  // already carries a note_id — the same isCreateOperation split the backend
  // action makes (see internal/workflow/actions/data_edit.go).
  const canActOnTask = (task: TaskData) => (task.note_id ? p.canEditData : p.canAddData);
  const canEditSelected = p.selectedTask ? canActOnTask(p.selectedTask) : false;

  if (p.isLoading) return <LoadingSpinner message="Loading task group..." />;

  if (!p.isLoading && !p.canEdit && !p.hasEditPermission) {
    return (
      <PageContainer title="View Data Group" breadcrumbs={p.breadcrumbs}>
        <EmptyState
          icon={<Icon as={FiLock} boxSize={12} />}
          title="View Only Mode"
          description="You don't have permission to edit tasks. Contact your administrator for access."
          action={{ label: 'Back to Data', onClick: p.handleBack }}
        />
      </PageContainer>
    );
  }

  if (!p.taskGroup) {
    return (
      <PageContainer title="Task Group Not Found" breadcrumbs={p.breadcrumbs}>
        <EmptyState
          icon={<Icon as={FiArrowLeft} boxSize={12} />}
          title="Data group not found"
          description="The requested data group could not be found."
          action={{ label: 'Back to Data', onClick: p.handleBack }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={`Edit Data Group - ${p.taskGroup.user_name}`} breadcrumbs={p.breadcrumbs}>
      <VStack spacing={6} align="stretch" maxW="4xl" mx="auto">
        <Card>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="text-secondary"><strong>User:</strong> {p.taskGroup.user_name}</Text>
                  <Text fontSize="sm" color="text-secondary"><strong>Date:</strong> {formatters.date(p.taskGroup.date)}</Text>
                </VStack>
                <Badge colorScheme="purple" fontSize="sm">{p.taskGroup.source_key}</Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="text-primary">Progress</Text>
                <Badge colorScheme="brand" fontSize="sm">{p.completedTasks} / {p.totalTasks} completed</Badge>
              </HStack>
              <Progress value={p.progress} colorScheme="brand" size="sm" borderRadius="full" />
            </VStack>
          </CardBody>
        </Card>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {p.tasks.map((task, index) => {
            const status = getTaskStatus(task);
            const canAct = canActOnTask(task);
            return (
              <TaskCard
                key={task.id}
                title={task.name}
                number={index + 1}
                status={status}
                displayValue={getDisplayValue(task)}
                editCount={task.edit_history?.length || 0}
                onClick={() => p.handleOpenTask(task)}
                actionLabel={status === 'blocked' ? 'View' : canAct ? (task.note_id ? 'Edit' : 'Add') : 'View'}
                actionTooltip={
                  status === 'blocked' ? 'This task is read-only'
                    : canAct ? (task.note_id ? 'Edit task value' : 'Add task value')
                    : 'View task details'
                }
              />
            );
          })}
        </SimpleGrid>

        <Button variant="outline" leftIcon={<Icon as={FiArrowLeft} />} onClick={p.handleBack}>
          Back to Data List
        </Button>
      </VStack>

      <TaskDataEditModal
        isOpen={p.isOpen}
        onClose={p.handleCloseModal}
        task={p.selectedTask}
        botCode={p.chatbot?.short_code || p.botId || ''}
        onSubmit={p.handleSubmit}
        isLoading={p.isSubmitting}
        canEdit={canEditSelected}
      />
    </PageContainer>
  );
}
